/**
 * Agendas Hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchAgendas,
  fetchAgenda,
  createAgenda,
  updateAgenda,
  deleteAgenda,
  addSection,
  updateSection,
  deleteSection,
  reorderSections,
} from '../api';
import type {
  AgendasQueryParams,
  CreateAgendaInput,
  CreateSectionInput,
} from '../types';

// =============================================================================
// Query Key Factory
// =============================================================================

export const agendasKeys = {
  all: ['meetings-agendas'] as const,
  lists: () => [...agendasKeys.all, 'list'] as const,
  list: (params: AgendasQueryParams) => [...agendasKeys.lists(), params] as const,
  details: () => [...agendasKeys.all, 'detail'] as const,
  detail: (id: string) => [...agendasKeys.details(), id] as const,
};

// =============================================================================
// Query Hooks
// =============================================================================

export function useAgendas(params: AgendasQueryParams = {}) {
  return useQuery({
    queryKey: agendasKeys.list(params),
    queryFn: () => fetchAgendas(params),
  });
}

export function useAgenda(id: string | undefined) {
  return useQuery({
    queryKey: agendasKeys.detail(id!),
    queryFn: () => fetchAgenda(id!),
    enabled: !!id,
  });
}

// =============================================================================
// Mutation Hooks
// =============================================================================

export function useCreateAgenda() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateAgendaInput) => createAgenda(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: agendasKeys.lists() });
    },
  });
}

export function useUpdateAgenda() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<CreateAgendaInput> }) =>
      updateAgenda(id, input),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: agendasKeys.lists() });
      queryClient.invalidateQueries({ queryKey: agendasKeys.detail(data.id) });
    },
  });
}

export function useDeleteAgenda() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteAgenda(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: agendasKeys.lists() });
      queryClient.removeQueries({ queryKey: agendasKeys.detail(id) });
    },
  });
}

export function useAddSection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ agendaId, input }: { agendaId: string; input: CreateSectionInput }) =>
      addSection(agendaId, input),
    onSuccess: (_data, { agendaId }) => {
      queryClient.invalidateQueries({ queryKey: agendasKeys.detail(agendaId) });
    },
  });
}

export function useUpdateSection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      agendaId,
      sectionId,
      input,
    }: {
      agendaId: string;
      sectionId: string;
      input: Partial<CreateSectionInput>;
    }) => updateSection(agendaId, sectionId, input),
    onSuccess: (_data, { agendaId }) => {
      queryClient.invalidateQueries({ queryKey: agendasKeys.detail(agendaId) });
    },
  });
}

export function useDeleteSection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ agendaId, sectionId }: { agendaId: string; sectionId: string }) =>
      deleteSection(agendaId, sectionId),
    onSuccess: (_data, { agendaId }) => {
      queryClient.invalidateQueries({ queryKey: agendasKeys.detail(agendaId) });
    },
  });
}

export function useReorderSections() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ agendaId, sectionIds }: { agendaId: string; sectionIds: string[] }) =>
      reorderSections(agendaId, sectionIds),
    onSuccess: (_data, { agendaId }) => {
      queryClient.invalidateQueries({ queryKey: agendasKeys.detail(agendaId) });
      queryClient.invalidateQueries({ queryKey: agendasKeys.lists() });
    },
  });
}
